/**
 * Channels + status for the Incoming tab, on one poll.
 *
 * Batched into a single query rather than two so each tick is one atomic burst
 * of requests. Two independent timers drifting into anti-phase would halve the
 * quiet window Playwright's `networkidle` needs.
 *
 * No `selectedTenantId` in the query key, deliberately: these packets belong to
 * a process, not a tenant.
 */
import { useQuery } from '@tanstack/react-query'
import { dataLayerApi } from '@/api/dataLayerApi'
import type { Hl7Channel, Hl7Status } from '@/lib/dataLayer/types'

/**
 * 1.5s. Fast enough that a packet feels live, slow enough to leave well over
 * the 500ms of quiet `waitForLoadState('networkidle')` waits for — see
 * `e2e/flows/integrations.flow.ts`. Do not drop this below 1000ms without
 * re-checking that; at 500ms the e2e helpers hang until their 60s timeout.
 */
export const DATA_LAYER_POLL_MS = 1500

export interface DataLayerOverview {
  channels: Hl7Channel[]
  status: Hl7Status
}

export const dataLayerOverviewKey = ['data-layer', 'overview'] as const

export function useDataLayerOverview(enabled: boolean) {
  return useQuery<DataLayerOverview>({
    queryKey: dataLayerOverviewKey,
    queryFn: async () => {
      const [channels, status] = await Promise.all([
        dataLayerApi.listChannels(),
        dataLayerApi.getStatus(),
      ])
      return { channels: channels.channels, status }
    },
    enabled,
    // Halt the poll while in error state instead of re-dialling a dead source
    // every tick (each failed tick costs the proxy a full upstream timeout).
    // Only an explicit Retry (invalidate) or a source switch (remove) resumes
    // it, which is also what the banner promises the user.
    refetchInterval: (query) => (query.state.status === 'error' ? false : DATA_LAYER_POLL_MS),
    // The app-wide default is 5 minutes, which would serve a stale strip on
    // every remount. This is a live view.
    staleTime: 0,
    // The app-wide `retry: 1` doubles every failed tick against a dead harness
    // and delays the unreachable state by a whole retry cycle.
    retry: false,
  })
}
